// ── Hydra Unabated Odds Stream — SSE client wrapper ────────────────
//
// One EventSource per game subscribed to `/api/v1/odds-stream/:gameKey`.
// Used by GameDetail's live/pre view to drive the consensus chart, the
// score/status header, and (eventually) a staleness/backpressure
// indicator.
//
// The hydra endpoint deliberately sits outside the Kalshi/Poly firehose
// — see HYDRA_API.md → "Unabated Odds Stream" for the design rationale.
// Hydra has a 256-listener cap; this wrapper exposes a single `close()`
// so route-leaving effects can deterministically drop the subscription
// without leaking sockets across navigation.
//
// VITE_HYDRA_ANALYTICS_URL is the hydra root. We use the
// /api/analytics/* namespace specifically because that's the only path
// hydra registers @fastify/cors against — `/api/v1/*` is intentionally
// bare for same-origin server-side consumers and a browser EventSource
// from g1.* would fail the preflight. Hydra exposes the same SSE
// handler under both prefixes; the analytics-prefixed path is the one
// we can actually reach from a deployed origin. See the 2026-05-06
// fee-endpoint precedent + the 2026-05-21 odds-stream / chart-stream
// dual-path landing in HYDRA_API.md.

const HYDRA_BASE = import.meta.env.VITE_HYDRA_ANALYTICS_URL as string | undefined;

// ─── Event shapes (mirrors HYDRA_API.md) ───────────────────────────

export interface OddsStreamGame {
  gameKey: string;
  eventId: number;
  sport: string;
  leagueId: number;
  awayTeam: string;
  homeTeam: string;
  eventStart: string;
}

export interface OddsStreamGameState {
  statusId: number;             // 1=scheduled, 2=live, 3=final
  gameClock?: string;           // sport-specific
  periodTypeId?: number;        // see /api/v1/period-types
  /** Hydra-resolved long-form name from Unabated's period-type catalog:
   *  "Full Game", "Ninth Inning", "First Quarter", etc. Always present
   *  when hydra knows the code; falls back to "Period <id>" for catalog
   *  misses. Added 2026-05-22 — see hydra-ask-period-label-string. */
  periodLabel?: string;
  awayScore?: number;
  homeScore?: number;
  // Sport-specific extras (balls/strikes/outs for MLB, period for NHL, …)
  // pass through verbatim; consumers cast based on game.sport.
  [key: string]: unknown;
}

export interface OddsStreamConsensus {
  periodTypeId: number;
  /** Long-form period name from Unabated's catalog — "Full Game",
   *  "Ninth Inning", "First Quarter", etc. Always populated by hydra
   *  (catalog miss falls back to "Period <id>"). Was abbreviated
   *  pre-2026-05-22 ("Game"); upgraded alongside the gameState
   *  periodLabel rollout. */
  periodLabel: string;
  awayFairProbCents: number;
  homeFairProbCents: number;
  /** Soccer 3-way (Unabated betTypeId 98): the Draw side's fair
   *  probability — away + home + draw = 100. Absent on binary sports. */
  drawFairProbCents?: number | null;
  contributingBooks: number;
  totalBooksSeen?: number;
  droppedBooks?: number;
  medianVigPct?: number;
}

export interface OddsStreamConsensusHistoryPoint {
  ts: number;
  periodTypeId: number;
  awayFairProbCents: number;
  homeFairProbCents: number;
  /** Soccer 3-way — see OddsStreamConsensus.drawFairProbCents. */
  drawFairProbCents?: number | null;
  contributingBooks: number;
}

/** A single per-source line entry — one row per
 *  (marketSourceId × betTypeId × periodTypeId × sideIndex) tuple.
 *  Spec from HYDRA_API.md → snapshot.lines / line event. */
export interface OddsStreamLine {
  marketSourceId: number;
  betTypeId: number;      // 1=ML, 2=spread, 3=total, 98=soccer 3-way
  periodTypeId: number;   // 1=full game; see /api/v1/period-types
  sideIndex: number;      // 0=away, 1=home, 2=draw (soccer 3-way)
  teamId?: number;
  price: number;          // American odds
  impliedProbCents: number;
  modifiedOn?: string;
  lastUpdateMs: number;
}

export interface OddsStreamLineEvent {
  type: 'line';
  line: OddsStreamLine;
  serverTs: number;
}

export interface OddsStreamSnapshot {
  type: 'snapshot';
  game: OddsStreamGame;
  gameState: OddsStreamGameState;
  lines: OddsStreamLine[];
  consensus: OddsStreamConsensus[];
  consensusHistory: OddsStreamConsensusHistoryPoint[];
  serverTs: number;
}

export interface OddsStreamConsensusEvent {
  type: 'consensus';
  consensus: OddsStreamConsensus[];
  serverTs: number;
}

export interface OddsStreamGameStateEvent {
  type: 'gameState';
  state: OddsStreamGameState;
  serverTs: number;
}

export interface OddsStreamHeartbeat {
  type: 'heartbeat';
  serverTs: number;
  consensus: OddsStreamConsensus[];
  droppedDueToBackpressure: number;
}

// ─── Handlers + lifecycle ──────────────────────────────────────────

export interface OddsStreamHandlers {
  onSnapshot: (snapshot: OddsStreamSnapshot) => void;
  onConsensus: (event: OddsStreamConsensusEvent) => void;
  onGameState: (event: OddsStreamGameStateEvent) => void;
  onHeartbeat: (event: OddsStreamHeartbeat) => void;
  /** Per-source line update (one book moved). Optional — consumers that
   *  only care about consensus can omit. Used by the /sources page to
   *  feed the per-book table + sparklines. */
  onLine?: (event: OddsStreamLineEvent) => void;
  onError?: (err: Event) => void;
  /** Fired right after `new EventSource(...)` succeeds. Useful for moving
   *  the hook out of the 'connecting' state into 'open'. */
  onOpen?: () => void;
}

export interface OddsStreamHandle {
  close(): void;
  /** Readonly URL the EventSource was opened against — useful for debug
   *  logging when stuff goes sideways. */
  url: string;
}

function buildStreamUrl(gameKey: string): string {
  if (!HYDRA_BASE) {
    throw new Error('VITE_HYDRA_ANALYTICS_URL not set — cannot open odds stream');
  }
  const root = HYDRA_BASE.replace(/\/$/, '');
  // `/api/analytics/odds-stream/` (not `/api/v1/`) — that's the
  // CORS-enabled path. EventSource can't set Authorization headers so
  // we pass nothing; hydra serves these unauthenticated by design.
  return `${root}/api/analytics/odds-stream/${encodeURIComponent(gameKey)}`;
}

/**
 * Open a single SSE subscription for `gameKey`. Returns a handle whose
 * `close()` MUST be called when the consumer unmounts — hydra caps total
 * listeners at 256 across all clients on the host.
 *
 * Handlers receive parsed event payloads; JSON parse / type-narrowing is
 * done here so consumers don't repeat it.
 */
export function openOddsStream(
  gameKey: string,
  handlers: OddsStreamHandlers,
): OddsStreamHandle {
  const url = buildStreamUrl(gameKey);
  const source = new EventSource(url, { withCredentials: false });

  source.addEventListener('open', () => {
    handlers.onOpen?.();
  });

  // Hydra emits all event types as default `message` events, discriminated
  // by the `type` field inside the JSON payload — NOT via SSE's `event:`
  // header (verified via curl 2026-05-22). Dispatch on the parsed type.
  source.addEventListener('message', (e) => {
    let payload: { type?: string } | null = null;
    try {
      payload = JSON.parse((e as MessageEvent).data);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[oddsStream] message parse failed', err);
      return;
    }
    if (!payload || typeof payload.type !== 'string') return;
    switch (payload.type) {
      case 'snapshot':
        handlers.onSnapshot(payload as unknown as OddsStreamSnapshot);
        break;
      case 'consensus':
        handlers.onConsensus(payload as unknown as OddsStreamConsensusEvent);
        break;
      case 'gameState':
        handlers.onGameState(payload as unknown as OddsStreamGameStateEvent);
        break;
      case 'heartbeat':
        handlers.onHeartbeat(payload as unknown as OddsStreamHeartbeat);
        break;
      case 'line':
        // Per-book line updates. High-frequency; only the /sources page
        // attaches a handler. Other consumers (consensus chart) leave
        // onLine unset and pay nothing.
        handlers.onLine?.(payload as unknown as OddsStreamLineEvent);
        break;
      default:
        // eslint-disable-next-line no-console
        console.warn('[oddsStream] unknown event type', payload.type);
    }
  });

  source.addEventListener('error', (e) => {
    // EventSource auto-reconnects on transient network errors. A persistent
    // error (404 missing gameKey, 503 listener-cap exceeded) would close
    // permanently. Surface to the caller so they can render a banner.
    // eslint-disable-next-line no-console
    console.warn('[oddsStream] error', { gameKey, readyState: source.readyState });
    handlers.onError?.(e);
  });

  return {
    url,
    close: () => {
      source.close();
    },
  };
}
